import {
  useIntl,
  IntlProvider,
  FormattedNumber,
  FormattedNumberParts,
} from 'react-intl';
import ReactHtmlParser from 'react-html-parser';

import React from 'react';
import Promo from '../../../elements/PromoTag';

import { Offer } from '../../../../ContentfulTypes';
import {
  CarrierOfferWrapper,
  CollapsedContainer,
  ButtonWrapper,
  RadioButton,
  TelecomProvider,
  TelecomLogo,
  UpFrontPrice,
  MonthlyPrice,
  Price,
  DetailLabel,
  PromoWrapper,
  NoPromo,
} from './styles';

interface Props {
  offer: Offer;
  labels: Record<string, string>;
  clickHandler: (offer: Offer) => void;
  selectedCarrier?: string;
  ariaLabel?: string;
}
const CarrierOffer = ({
  offer,
  labels,
  clickHandler,
  selectedCarrier = '',
  ariaLabel = '',
}: Props) => {
  const intl = useIntl();
  const { cmsName } = offer.phoneCarrier;
  const points = offer.pcoPoints > 0 && new Intl.NumberFormat().format(offer.pcoPoints);

  return (
    <CarrierOfferWrapper
      data-testid="carrier-offer"
      id={cmsName}
      onClick={() => {
        clickHandler(offer);
      }}
      // Enhance aria label to read out all contents and value of the button
      aria-label={`${ReactHtmlParser(ariaLabel)} - ${
        // carrier name
        cmsName
        // upfront cost price
      } - $${offer.carrierUpfrontPrice.toFixed(2)} ${
        // upfront cost text
        labels['6KcxYb1EQRl64daz8jTntc']
        // per month price
      } - $${offer.monthlyPrice.toFixed(2)} ${
        // per month text
        labels.aOC9eaI5Lh49zWX8rqb2Y
      } - 
      ${
        // points earned number read by screen reader if points are available
        points
          ? `${points} points ${
            // points earned text
            labels['2TzLWT8BmcdnnFz6XPkKCA']
          }`
          : ''
      }`}
      className="carrierListItem"
      aria-checked={cmsName === selectedCarrier}
    >
      <CollapsedContainer>
        {/* RADIO BUTTON */}
        <ButtonWrapper>
          <RadioButton className="radioStyle" />
        </ButtonWrapper>

        {/* LOGO */}
        <TelecomProvider>
          <TelecomLogo image={offer.phoneCarrier.logo} />
        </TelecomProvider>

        {/* UPFRONT PRICE */}
        <UpFrontPrice>
          <Price className="carrierUpFrontPriceBlade">
            <IntlProvider locale={`${intl.locale}-CA`}>
              {intl.locale === 'en' ? (
                <FormattedNumber
                  value={offer.carrierUpfrontPrice}
                  // eslint-disable-next-line react/style-prop-object
                  style="currency"
                  currency="CAD"
                  maximumFractionDigits={2}
                  minimumFractionDigits={2}
                />
              ) : (
                <FormattedNumberParts
                  value={offer.carrierUpfrontPrice}
                  // eslint-disable-next-line react/style-prop-object
                  style="currency"
                  currency="CAD"
                >
                  {(parts) => (
                    <>
                      {parts[0].value}
                      {parts[1].value}
                      {parts[2].value}
                      &nbsp;$
                    </>
                  )}
                </FormattedNumberParts>
              )}
            </IntlProvider>

            {/* Add asterisks if needed for legal text at bottom of panel */}
            {offer.phoneCarrierTier.description.raw.includes('*') ? '*' : ''}
          </Price>
          <DetailLabel>{labels['6KcxYb1EQRl64daz8jTntc']}</DetailLabel>
        </UpFrontPrice>

        {/* MONTHLY PRICE */}
        <MonthlyPrice>
          <Price className="monthlyPriceBlade">
            <IntlProvider locale={`${intl.locale}-CA`}>
              {intl.locale === 'en' ? (
                <FormattedNumber
                  value={offer.monthlyPrice}
                  // eslint-disable-next-line react/style-prop-object
                  style="currency"
                  currency="CAD"
                  maximumFractionDigits={2}
                  minimumFractionDigits={2}
                />
              ) : (
                <FormattedNumberParts
                  value={offer.monthlyPrice}
                  // eslint-disable-next-line react/style-prop-object
                  style="currency"
                  currency="CAD"
                >
                  {(parts) => (
                    <>
                      {parts[0].value}
                      {parts[1].value}
                      {parts[2].value}
                      &nbsp;$
                    </>
                  )}
                </FormattedNumberParts>
              )}
            </IntlProvider>
          </Price>
          <DetailLabel>{labels.aOC9eaI5Lh49zWX8rqb2Y}</DetailLabel>
        </MonthlyPrice>

        {/* PCO POINTS */}
        {offer.pcoPoints > 0 ? (
          <PromoWrapper>
            <Promo
              isDetailPage
              bestValueOffer={offer.pcoPoints}
            />
            <DetailLabel>{labels['2TzLWT8BmcdnnFz6XPkKCA']}</DetailLabel>
          </PromoWrapper>
        ) : (
          <NoPromo />
        )}
      </CollapsedContainer>
    </CarrierOfferWrapper>
  );
};

export default CarrierOffer;
